import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getAccessToken, getRefreshToken, setAuthTokens, clearAuthTokens } from './storage'
import { parseApiErrorResponse } from './errors'
import { currentLocale } from '@/i18n'

const DEFAULT_API_BASE_URL = 'https://pg-pharma-dev.jsnp.io'

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '')
}

export const apiBaseUrl = normalizeBaseUrl(
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || DEFAULT_API_BASE_URL
)

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

const rawClient = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    Accept: 'application/json',
  },
})

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    Accept: 'application/json',
  },
})

let refreshInFlight: Promise<void> | null = null

async function refreshTokens(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  const response = await rawClient.post<{
    accessToken: string
    refreshToken: string
    expiresAt: string
  }>('/api/v1/auth/refresh', { refreshToken })

  setAuthTokens({
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
    expiresAt: response.data.expiresAt,
  })
}

apiClient.interceptors.request.use((config) => {
  config.headers = config.headers ?? {}

  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Tell the backend which language to localize error responses in. The backend
  // reads `x-language` per request (see backend shared/i18n error localization).
  // The persisted profile-level preference (PSP-354/PSP-355) is applied to the
  // active locale at login/app load, so this header follows it automatically.
  config.headers['x-language'] = currentLocale()

  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = (error.config ?? {}) as RetryableRequestConfig
    const status = error.response?.status

    if (status !== 401 || config._retry) {
      throw error
    }

    const apiError = parseApiErrorResponse(error.response?.data)
    const shouldAttemptRefresh = apiError?.code === 'TOKEN_EXPIRED' || apiError?.code === 'AUTH_REQUIRED' || !apiError

    const refreshToken = getRefreshToken()
    if (!shouldAttemptRefresh || !refreshToken) {
      clearAuthTokens()
      throw error
    }

    config._retry = true

    if (!refreshInFlight) {
      refreshInFlight = refreshTokens().finally(() => {
        refreshInFlight = null
      })
    }

    try {
      await refreshInFlight
    } catch {
      clearAuthTokens()
      throw error
    }

    const newAccessToken = getAccessToken()
    if (newAccessToken) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${newAccessToken}`
    }

    return apiClient.request(config)
  }
)
